import React from "react";

// The Terms of Membership shown on the /membership/terms page, before the member pays.

const LAST_UPDATED = "17 September 2026";

const SECTIONS = [
    {
        title: "1. About these Terms",
        body: [
            "These Terms of Membership are an agreement between you and Schmodel Limited (\"Schmodel\", \"we\", \"us\"). They set out what your membership of Schmodel.Club includes, what you pay for it, and the rules you agree to follow as a member.",
            "They apply alongside our End User Licence Agreement, our Privacy Policy and, if you open a shop, the Schmodel.Shop Terms and Conditions. Where these Terms of Membership and the End User Licence Agreement cover the same subject, these Terms apply to your membership and your fee.",
            "By accepting these Terms you confirm that you have read them and that you agree to be bound by them from the moment your first payment is taken.",
        ],
    },
    {
        title: "2. Who can be a member",
        body: [
            "Schmodel.Club is a private, members-only community for the fashion and beauty industry. It is not open to the general public, and membership is granted at our discretion.",
            "To hold a membership you must be at least 18 years old, have applied and been approved by us, keep an Instagram account connected to your Schmodel profile, and pay the applicable Membership Fee.",
            "Your membership is personal to you and cannot be shared, transferred or sold. You are responsible for everything done through your account, so keep your login and magic links private.",
        ],
    },
    {
        title: "3. What your membership includes",
        body: [
            "What you can access depends on the membership you were accepted for, and is shown to you before you pay:",
        ],
        list: [
            "Icon .app — a profile in the community, your own luxury shop, and access to brands and jobs.",
            "Icon Plus .app — full app access, Schmodel.studio clubs, and member-only offers.",
            "Brand .app — the tools to post jobs, send gifts and offers, and connect with approved Icons.",
        ],
        after: [
            "We develop the platform continuously. Features may be added, changed or withdrawn, and we will not reduce the core benefits of your membership during a period you have already paid for without offering you a fair remedy.",
            "We provide the platform as a neutral technology provider. We are not an agency, an employer or an intermediary, and we do not promise you any particular job, booking, match, collaboration or income.",
        ],
    },
    {
        title: "4. The Membership Fee",
        body: [
            "The Membership Fee is the annual amount shown to you at checkout, in pounds sterling, and covers twelve months of membership from the day your first payment succeeds.",
            "Payments are handled by Stripe. We do not see or store your card details. By paying, you authorise us to charge the same payment method for each renewal until you cancel.",
            "Unless the law requires otherwise, the fee is inclusive of any applicable taxes shown at checkout.",
        ],
    },
    {
        title: "5. Renewal",
        body: [
            "Your membership renews automatically each year on the anniversary of your first payment, for a further twelve months, at the fee then in force.",
            "If we change the Membership Fee, we will tell you by email at least 30 days before the change takes effect. The new fee applies from your next renewal, and you are free to cancel before then if you do not accept it.",
            "We will remind you before each renewal so that a charge never comes as a surprise.",
        ],
    },
    {
        title: "6. Cancelling your membership",
        body: [
            "If you are a consumer, you have 14 days from the day your membership starts to change your mind and cancel for any reason. Because your access begins immediately at your request, we may keep a proportionate amount of the fee for the days you were a member before cancelling.",
            "After that period you may cancel at any time. Cancellation takes effect at the end of the twelve-month period you have paid for, and we do not refund the remainder of that period.",
            "To cancel, contact us through the support channels in the app or email legal@schmodel.com from the address on your account. Nothing here affects your statutory rights.",
        ],
    },
    {
        title: "7. If a payment fails",
        body: [
            "If a renewal payment fails, we will try again and ask you to update your payment method. Your access may be limited while a payment is outstanding.",
            "If the fee remains unpaid, your membership ends and your profile, posts and shop stop being visible to other members. You can apply to rejoin later, and we may ask you to go through review again.",
        ],
    },
    {
        title: "8. Member Engagements",
        body: [
            "A \"Member Engagement\" is any booking, job, collaboration, gift, offer, sale or other arrangement agreed between members, whether it is arranged through the platform or follows an introduction made on it.",
            "Every Member Engagement is a contract between the members involved. Schmodel is not a party to it. We are not responsible for the conduct of either side, for the quality or delivery of any work, for any payment between members, or for any tax, licence or permit that arrangement requires.",
            "Agree the important terms in writing before you start: what is being done, when, where, for how much, and who owns the resulting images. Take care when meeting anyone in person and when sharing personal contact details.",
        ],
    },
    {
        title: "9. What you post",
        body: [
            "Members post ads, job posts, gifts and offers. Everything you post must be accurate, lawful, and genuinely offered — do not advertise work, budgets, gifts or products you cannot honour.",
            "You must have the right to use every image you upload, including the permission of anyone appearing in it and of the photographer. Do not upload anything you did not create or licence.",
            "Some posts are reviewed before they are shown to other members. We may decline, edit the visibility of, or remove any post, and we may limit how many members can respond to a gift or offer.",
        ],
    },
    {
        title: "10. Your content",
        body: [
            "You keep ownership of the photographs, text and other material you upload. You give us a non-exclusive, worldwide licence to host, store, reproduce and display that material for the purpose of operating and promoting the platform, for as long as you remain a member.",
            "This licence ends when your content is removed, except where we must keep a copy to meet a legal obligation or to resolve a dispute. Removing content may take a short time to take effect across the platform.",
        ],
    },
    {
        title: "11. Messaging and calls",
        body: [
            "Members can message and call each other in the app. Use these features for genuine professional contact only.",
            "Do not send unsolicited commercial messages, harass anyone, or share another member's contact details, images or conversations outside the platform without their permission.",
            "We may freeze a conversation when the engagement behind it ends, or when we are investigating a report.",
        ],
    },
    {
        title: "12. Conduct and moderation",
        body: [
            "We expect members to behave professionally and lawfully. Do not impersonate anyone, misrepresent your experience or following, use the platform to defraud another member, or attempt to access parts of it you have no right to.",
            "Where a member breaches these Terms we may issue a warning, remove content, restrict features, suspend the account, or end the membership. For serious breaches we may act immediately and without notice.",
            "If we end your membership because of a breach, no refund is due. If we end it for any other reason, we will refund the unused part of your fee.",
            "If another member behaves badly towards you, report it in the app or to legal@schmodel.com. We investigate every report.",
        ],
    },
    {
        title: "13. Schmodel.Shop",
        body: [
            "Schmodel.Shop is a pre-loved luxury marketplace operated by Schmodel.Shop Limited. Opening a shop, listing items and selling through it are governed by the Schmodel.Shop Terms and Conditions, which you accept separately.",
            "Your Membership Fee does not cover any commission, listing cost or payment charge that applies to shop sales. Those are set out in the shop terms.",
        ],
    },
    {
        title: "14. Your data",
        body: [
            "How we collect and use your personal data, including the profile and identity information you give us and the data we receive when you connect Instagram, is explained in our Privacy Policy.",
            "Members can see the parts of your profile that your membership makes visible to them. Think before adding anything to your profile that you would not want other members to see.",
        ],
    },
    {
        title: "15. Changes to these Terms",
        body: [
            "We may update these Terms to reflect changes to the platform or to the law. If a change materially affects your rights, we will tell you by email at least 30 days before it applies, and it will normally take effect at your next renewal.",
            "If you do not accept a change, you may cancel before it takes effect. Continuing to use the platform after that date means you accept the updated Terms.",
        ],
    },
    {
        title: "16. Liability",
        body: [
            "Nothing in these Terms limits our liability for death or personal injury caused by our negligence, for fraud, or for anything else that cannot lawfully be limited.",
            "Subject to that, our total liability to you in connection with your membership is limited to the Membership Fees you paid us in the twelve months before the claim arose.",
            "We are not liable for what happens in a Member Engagement, for the acts of other members, or for loss of profit, opportunity or reputation.",
        ],
    },
    {
        title: "17. Contacting us",
        body: [
            "These Terms are governed by the laws of England and Wales, and the courts of England and Wales have exclusive jurisdiction, subject to any mandatory consumer protections in your country of residence.",
            "For anything about your membership, your fee or these Terms, email legal@schmodel.com or contact us through the support channels in the app.",
        ],
    },
];

function TermsOfMembership() {
    return (
        <>
            <p className="terms-body">Last updated: {LAST_UPDATED}</p>
            {SECTIONS.map(({ title, body, list, after }) => (
                <div key={title}>
                    <p className="terms-heading">{title}</p>
                    {body.map((paragraph, i) => (
                        <p className="terms-body" key={i}>{paragraph}</p>
                    ))}
                    {list && (
                        <ul className="terms-list">
                            {list.map((item, i) => <li key={i}>{item}</li>)}
                        </ul>
                    )}
                    {after && after.map((paragraph, i) => (
                        <p className="terms-body" key={i}>{paragraph}</p>
                    ))}
                </div>
            ))}
        </>
    );
}

export default TermsOfMembership;
